<template>
  <div style="display: flex; flex-direction: column; height: 100%; overflow: hidden;">
    <div
      style="flex: 1; position: relative; overflow: hidden; background: #ececec;"
      ref="canvasContainer"
    >
      <canvas
        ref="canvas"
        @mousedown="onMouseDown"
        @mousemove="onMouseMove"
        @mouseup="onMouseUp"
        @mouseleave="onMouseLeave"
        @contextmenu.prevent="onRightClick"
        @wheel.prevent="onWheel"
        @touchstart.prevent="onTouchStart"
        @touchmove.prevent="onTouchMove"
        @touchend.prevent="onTouchEnd"
        style="display: block; position: absolute; top: 0; left: 0;"
      ></canvas>

      <div v-if="!plano" class="ipv-canvas-placeholder">
        <v-icon size="56" color="grey lighten-1">mdi-image-outline</v-icon>
        <div class="text-subtitle-2 mt-3 grey--text">Seleccione un plano para continuar</div>
      </div>

      <div v-if="loading" class="ipv-loading-indicator">
        <v-progress-circular
          indeterminate
          color="#2D4B95"
          size="56"
        ></v-progress-circular>
        <div class="text-caption mt-3 grey--text">Cargando plano ...</div>
      </div>

      <div v-if="isPanning" class="ipv-pan-indicator">
        <v-icon small>mdi-pan</v-icon>
        <span class="text-caption ml-1">Moviendo vista</span>
      </div>
    </div>
  </div>
</template>


<script>
export default {
  name: 'ImagePolygonViewer',

  props: {
    plano: {
      type: Object,
      default: null
    },
    planoData: {
      type: Object,
      default: null
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    allPolygons: {
      type: Array,
      default: () => []
    },
    lineColor: {
      type: String,
      default: '#2D4B95'
    },
    fillOpacity: {
      type: Number,
      default: 0.3
    }
  },

  data () {
    return {
      nodes: [],
      imageWidth: 0,
      imageHeight: 0,
      draggingNodeIndex: null,
      context: null,
      backgroundImage: null,
      scale: 1,
      canvasWidth: 0,
      canvasHeight: 0,
      loading: false,
      hoverNode: null,
      nodeRadius: 10,
      nodeSensitivity: 1.5,
      zoomLevel: 1,
      minZoom: 0.25,
      maxZoom: 4,
      zoomStep: 0.1,
      panOffset: { x: 0, y: 0 },
      isPanning: false,
      lastPanPosition: { x: 0, y: 0 },
      mouseDownTime: 0,
      mouseDownPos: null,
      hasDragged: false,
      // touch
      touchStartTime: 0,
      touchStartPos: null,
      touchHasMoved: false,
      longPressFired: false,
      longPressTimer: null,
      pinchStartDistance: null,
      pinchStartZoom: 1
    }
  },

  computed: {
    effectiveNodeRadius () {
      return this.nodeRadius * this.nodeSensitivity / (this.scale * this.zoomLevel)
    },
    isAllPolygonsMode () {
      return this.allPolygons && this.allPolygons.length > 0 && this.readOnly
    },
    canEdit () {
      return !this.readOnly && !this.isAllPolygonsMode
    }
  },

  watch: {
    lineColor () {
      this.redraw()
    },

    fillOpacity () {
      this.redraw()
    },

    planoData: {
      immediate: true,
      handler (newVal) {
        if (newVal) {
          this.nodes = newVal.nodes ? [...newVal.nodes] : []
          this.$emit('nodes-change', this.nodes.length)
        }
      }
    },

    plano (newVal) {
      this.hoverNode = null
      this.draggingNodeIndex = null
      if (newVal) {
        this.$nextTick(() => this.loadImage())
      } else {
        this.backgroundImage = null
        this.nodes = []
        this.redraw()
      }
    },

    allPolygons () {
      this.redraw()
    }
  },

  mounted () {
    this.$nextTick(() => {
      this.resizeCanvas()
      window.addEventListener('resize', this.onWindowResize)
      if (this.plano) this.loadImage()
    })
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.onWindowResize)
  },

  methods: {
    onWindowResize () {
      this.resizeCanvas()
      this.redraw()
    },

    resizeCanvas () {
      const canvas    = this.$refs.canvas
      const container = this.$refs.canvasContainer
      if (!canvas || !container) return
      canvas.width  = container.clientWidth
      canvas.height = container.clientHeight
      this.canvasWidth  = canvas.width
      this.canvasHeight = canvas.height
      if (this.backgroundImage) {
        const scaleX = canvas.width  / this.imageWidth
        const scaleY = canvas.height / this.imageHeight
        this.scale = Math.min(scaleX, scaleY) * 0.95
      }
      this.redraw()
    },

    async savePlano () {
      if (this.nodes.length < 3) return
			this.$emit('save-plano', {
				nodes:   this.nodes,
				color:   this.lineColor,
				opacity: this.fillOpacity
			})
    },

    showSuccessMessage (message, title = 'Éxito') {
      this.$store.globalStore.dispatch('addMessage', {
        message, title, created: new Date(), type: 'success', icon: 'mdi-check-circle', color: '#3EBFA3'
      })
    },

    showErrorMessage (message) {
      this.$store.globalStore.dispatch('addMessage', {
        message, title: 'Error', created: new Date(), type: 'error', icon: 'mdi-alert-circle', color: '#fb8c00'
      })
    },

    loadImage () {
      const plano = this.plano
      if (!plano || !plano.file || !plano.file.pathmedia || !plano.file.pathmedia.path) {
        if (plano) console.error('[ImagePolygonViewer] Plano sin imagen:', plano.id)
        return
      }
      this.loading = true
      this.resetZoomAndPan()
      const image = new Image()
			image.crossOrigin = 'anonymous'
      image.onload = () => {
        this.imageWidth  = image.width
        this.imageHeight = image.height
        const canvas    = this.$refs.canvas
        const container = this.$refs.canvasContainer
        if (!canvas || !container) return
        canvas.width  = container.clientWidth
        canvas.height = container.clientHeight
        this.canvasWidth  = canvas.width
        this.canvasHeight = canvas.height
        const scaleX = canvas.width  / image.width
        const scaleY = canvas.height / image.height
        this.scale = Math.min(scaleX, scaleY) * 0.95
        this.context         = canvas.getContext('2d')
        this.backgroundImage = image
        if (this.planoData && this.planoData.nodes && this.planoData.nodes.length > 0) {
          this.nodes = [...this.planoData.nodes]
        } else if (!this.isAllPolygonsMode) {
          this.nodes = []
        }
        this.redraw()
        this.loading = false
      }
      image.onerror = () => {
        console.error('[ImagePolygonViewer] Failed to load image:', plano.file.pathmedia.path)
        this.loading = false
      }
      image.src = plano.file.pathmedia.path
    },

    getCanvasCoordinates (event) {
      const canvas = this.$refs.canvas
      const rect   = canvas.getBoundingClientRect()
      const clientX = event.clientX || (event.touches && event.touches[0] ? event.touches[0].clientX : 0)
      const clientY = event.clientY || (event.touches && event.touches[0] ? event.touches[0].clientY : 0)
      const mouseX = clientX - rect.left
      const mouseY = clientY - rect.top
      const scaledW = this.imageWidth  * this.scale * this.zoomLevel
      const scaledH = this.imageHeight * this.scale * this.zoomLevel
      const centerX = (canvas.width  - scaledW) / 2
      const centerY = (canvas.height - scaledH) / 2
      const adjX = mouseX - (centerX + this.panOffset.x * this.scale * this.zoomLevel)
      const adjY = mouseY - (centerY + this.panOffset.y * this.scale * this.zoomLevel)
      return {
        x: Math.max(0, Math.min(this.imageWidth,  Math.round(adjX / (this.scale * this.zoomLevel)))),
        y: Math.max(0, Math.min(this.imageHeight, Math.round(adjY / (this.scale * this.zoomLevel))))
      }
    },

    getNodeAtPosition (x, y) {
      const hitRadius = this.effectiveNodeRadius
      for (let i = 0; i < this.nodes.length; i++) {
        const node = this.nodes[i]
        const dist = Math.sqrt(Math.pow(node.x - x, 2) + Math.pow(node.y - y, 2))
        if (dist <= hitRadius) return i
      }
      return null
    },

    onMouseDown (event) {
      if (!this.plano) return
      this.mouseDownTime = Date.now()
      this.hasDragged    = false
      this.mouseDownPos  = { x: event.clientX, y: event.clientY }
      if (event.button === 1 || (event.button === 0 && event.shiftKey)) {
        this.isPanning = true
        this.lastPanPosition = { x: event.clientX, y: event.clientY }
        event.preventDefault()
        return
      }
      if (event.button === 0) {
        if (!this.canEdit) {
          this.isPanning = true
          this.lastPanPosition = { x: event.clientX, y: event.clientY }
          return
        }
        const { x, y }  = this.getCanvasCoordinates(event)
        const nodeIndex = this.getNodeAtPosition(x, y)
        if (nodeIndex !== null) {
          this.draggingNodeIndex = nodeIndex
          document.body.style.cursor = 'grabbing'
        } else {
          this.lastPanPosition = { x: event.clientX, y: event.clientY }
        }
      }
    },

    onMouseMove (event) {
      if (!this.plano) return
      const moved = this.mouseDownPos
        ? Math.abs(event.clientX - this.mouseDownPos.x) > 4 || Math.abs(event.clientY - this.mouseDownPos.y) > 4
        : false
      if (this.draggingNodeIndex !== null) {
        const { x, y } = this.getCanvasCoordinates(event)
        this.nodes[this.draggingNodeIndex] = { x, y }
        this.redraw()
        return
      }
      if (this.isPanning) {
        const dX = event.clientX - this.lastPanPosition.x
        const dY = event.clientY - this.lastPanPosition.y
        this.panOffset.x += dX / (this.scale * this.zoomLevel)
        this.panOffset.y += dY / (this.scale * this.zoomLevel)
        this.lastPanPosition = { x: event.clientX, y: event.clientY }
        this.redraw()
        return
      }
      if (event.buttons === 1 && moved && this.mouseDownPos && this.draggingNodeIndex === null) {
        this.isPanning = true
        this.hasDragged = true
        this.lastPanPosition = { x: event.clientX, y: event.clientY }
        document.body.style.cursor = 'grabbing'
        return
      }
      if (event.buttons === 0) {
        if (this.canEdit) {
          const { x, y } = this.getCanvasCoordinates(event)
          const nodeIndex = this.getNodeAtPosition(x, y)
          if (nodeIndex !== null) {
            document.body.style.cursor = 'grab'
            this.hoverNode = nodeIndex
          } else {
            document.body.style.cursor = 'crosshair'
            this.hoverNode = null
          }
        } else {
          document.body.style.cursor = 'grab'
        }
        this.redraw()
      }
    },

    onMouseUp (event) {
      if (
        !this.isPanning &&
        this.draggingNodeIndex === null &&
        this.mouseDownPos &&
        !this.hasDragged &&
        this.plano &&
        this.canEdit &&
        event.button === 0
      ) {
        const { x, y } = this.getCanvasCoordinates(event)
        this.nodes.push({ x, y })
        this.redraw()
        this.$emit('nodes-change', this.nodes.length)
      }
      this.draggingNodeIndex = null
      this.isPanning  = false
      this.hasDragged = false
      this.mouseDownPos = null
      document.body.style.cursor = 'default'
    },

    onMouseLeave () {
      this.draggingNodeIndex = null
      this.isPanning  = false
      this.hasDragged = false
      this.mouseDownPos = null
      this.hoverNode  = null
      document.body.style.cursor = 'default'
      this.redraw()
    },

    onRightClick (event) {
      if (!this.plano || !this.canEdit) return
      const { x, y }  = this.getCanvasCoordinates(event)
      const nodeIndex = this.getNodeAtPosition(x, y)
      if (nodeIndex !== null) {
        this.nodes.splice(nodeIndex, 1)
        this.redraw()
        this.$emit('nodes-change', this.nodes.length)
      }
    },

    redraw () {
      if (!this.context || !this.backgroundImage) return
      const canvas = this.$refs.canvas
      this.context.clearRect(0, 0, canvas.width, canvas.height)
      this.drawCheckerboard(canvas)
      this.context.save()
      const scaledW = this.imageWidth  * this.scale * this.zoomLevel
      const scaledH = this.imageHeight * this.scale * this.zoomLevel
      const centerX = (canvas.width  - scaledW) / 2
      const centerY = (canvas.height - scaledH) / 2
      this.context.translate(
        centerX + this.panOffset.x * this.scale * this.zoomLevel,
        centerY + this.panOffset.y * this.scale * this.zoomLevel
      )
      this.context.scale(this.scale * this.zoomLevel, this.scale * this.zoomLevel)
      this.context.drawImage(this.backgroundImage, 0, 0, this.imageWidth, this.imageHeight)
      if (this.allPolygons && this.allPolygons.length > 0) {
        this.allPolygons.forEach(polygon => {
          if (polygon.nodes && polygon.nodes.length > 0) {
            this.drawPolygon(polygon.nodes, polygon.color || '#2D4B95', polygon.opacity !== undefined ? polygon.opacity : 0.3, false)
          }
        })
      }
      if (!this.isAllPolygonsMode) {
        this.drawPolygon(this.nodes, this.lineColor, this.fillOpacity, true)
      }
      this.context.restore()
    },

    drawCheckerboard (canvas) {
      const size = 12
      this.context.fillStyle = '#d8d8d8'
      this.context.fillRect(0, 0, canvas.width, canvas.height)
      this.context.fillStyle = '#c8c8c8'
      for (let x = 0; x < canvas.width; x += size * 2) {
        for (let y = 0; y < canvas.height; y += size * 2) {
          this.context.fillRect(x, y, size, size)
          this.context.fillRect(x + size, y + size, size, size)
        }
      }
    },

    drawPolygon (nodes, color, opacity, showNodes = true) {
      if (!nodes || nodes.length === 0) return
      this.context.beginPath()
      this.context.moveTo(nodes[0].x, nodes[0].y)
      for (let i = 1; i < nodes.length; i++) {
        this.context.lineTo(nodes[i].x, nodes[i].y)
      }
      if (nodes.length >= 3) {
        this.context.closePath()
        this.context.fillStyle = this.hexToRgba(color, opacity)
        this.context.fill()
      }
      this.context.lineWidth   = 2 / (this.scale * this.zoomLevel)
      this.context.strokeStyle = color
      this.context.stroke()
      if (!showNodes) return
      nodes.forEach((node, index) => {
        const isHighlighted  = index === this.hoverNode || index === this.draggingNodeIndex
        const visualRadius   = (isHighlighted ? 8 : 6) / (this.scale * this.zoomLevel)
        this.context.beginPath()
        this.context.arc(node.x, node.y, visualRadius, 0, Math.PI * 2)
        this.context.fillStyle = isHighlighted ? color : '#ffffff'
        this.context.fill()
        this.context.lineWidth   = (isHighlighted ? 2.5 : 1.5) / (this.scale * this.zoomLevel)
        this.context.strokeStyle = color
        this.context.stroke()
        if (nodes.length <= 20) {
          const fontSize = Math.max(10 / (this.scale * this.zoomLevel), 8)
          this.context.fillStyle     = isHighlighted ? '#ffffff' : '#000000'
          this.context.font          = `bold ${fontSize}px sans-serif`
          this.context.textAlign     = 'center'
          this.context.textBaseline  = 'middle'
          this.context.fillText((index + 1).toString(), node.x, node.y)
        }
      })
    },

    hexToRgba (hex, alpha) {
      const code = hex.charAt(0) === '#' ? hex.substring(1) : hex
      const r = parseInt(code.substring(0, 2), 16)
      const g = parseInt(code.substring(2, 4), 16)
      const b = parseInt(code.substring(4, 6), 16)
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    },

    zoomIn () {
      if (this.zoomLevel >= this.maxZoom) return
      this.zoomLevel = Math.min(this.maxZoom, parseFloat((this.zoomLevel + this.zoomStep).toFixed(2)))
      this.redraw()
      this.$emit('zoom-change', this.zoomLevel)
    },

    zoomOut () {
      if (this.zoomLevel <= this.minZoom) return
      this.zoomLevel = Math.max(this.minZoom, parseFloat((this.zoomLevel - this.zoomStep).toFixed(2)))
      this.redraw()
      this.$emit('zoom-change', this.zoomLevel)
    },

    resetZoomAndPan () {
      this.zoomLevel = 1
      this.panOffset = { x: 0, y: 0 }
      this.redraw()
    },

    onWheel (event) {
      if (!this.plano) return
      event.preventDefault()
      const delta       = Math.sign(event.deltaY) * -1
      const coords      = this.getCanvasCoordinates(event)
      const prevZoom    = this.zoomLevel
      if (delta > 0) {
        this.zoomLevel = Math.min(this.maxZoom, parseFloat((this.zoomLevel + this.zoomStep).toFixed(2)))
      } else {
        this.zoomLevel = Math.max(this.minZoom, parseFloat((this.zoomLevel - this.zoomStep).toFixed(2)))
      }
      const zoomRatio = this.zoomLevel / prevZoom
      this.panOffset.x += coords.x * (zoomRatio - 1) / this.zoomLevel
      this.panOffset.y += coords.y * (zoomRatio - 1) / this.zoomLevel
      this.redraw()
      this.$emit('zoom-change', this.zoomLevel)
    },

    // ── Touch support ──────────────────────────────────────────────────────────

    _getTouchCoords (touch) {
      return this.getCanvasCoordinates({ clientX: touch.clientX, clientY: touch.clientY, touches: null })
    },

    _pinchDistance (t1, t2) {
      const dx = t1.clientX - t2.clientX
      const dy = t1.clientY - t2.clientY
      return Math.sqrt(dx * dx + dy * dy)
    },

    _cancelLongPress () {
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer)
        this.longPressTimer = null
      }
    },

    onTouchStart (event) {
      if (!this.plano) return
      const touches = event.touches

      if (touches.length === 2) {
        // Pinch-to-zoom: cancel any pending long press and start pinch tracking
        this._cancelLongPress()
        this.draggingNodeIndex = null
        this.isPanning = false
        this.pinchStartDistance = this._pinchDistance(touches[0], touches[1])
        this.pinchStartZoom = this.zoomLevel
        return
      }

      if (touches.length === 1) {
        const touch = touches[0]
        this.touchStartTime = Date.now()
        this.touchStartPos  = { x: touch.clientX, y: touch.clientY }
        this.touchHasMoved  = false
        this.pinchStartDistance = null

        const { x, y }  = this._getTouchCoords(touch)
        const nodeIndex = this.canEdit ? this.getNodeAtPosition(x, y) : null

        if (nodeIndex !== null) {
          this.draggingNodeIndex = nodeIndex
          // Long-press on a node → delete it
          this.longPressTimer = setTimeout(() => {
            this.longPressTimer = null
            if (!this.touchHasMoved && this.draggingNodeIndex !== null) {
              this.longPressFired = true
              this.nodes.splice(this.draggingNodeIndex, 1)
              this.draggingNodeIndex = null
              this.redraw()
              this.$emit('nodes-change', this.nodes.length)
            }
          }, 500)
        } else {
          // Will pan or add node on touchend
          this.isPanning = false
          this.lastPanPosition = { x: touch.clientX, y: touch.clientY }
        }
      }
    },

    onTouchMove (event) {
      if (!this.plano) return
      const touches = event.touches

      if (touches.length === 2 && this.pinchStartDistance !== null) {
        const dist  = this._pinchDistance(touches[0], touches[1])
        const ratio = dist / this.pinchStartDistance
        const newZoom = Math.min(this.maxZoom, Math.max(this.minZoom,
          parseFloat((this.pinchStartZoom * ratio).toFixed(2))
        ))
        this.zoomLevel = newZoom
        this.redraw()
        this.$emit('zoom-change', this.zoomLevel)
        return
      }

      if (touches.length === 1) {
        const touch = touches[0]
        const moved = this.touchStartPos
          ? Math.abs(touch.clientX - this.touchStartPos.x) > 8 ||
            Math.abs(touch.clientY - this.touchStartPos.y) > 8
          : false

        if (moved && !this.touchHasMoved) {
          this.touchHasMoved = true
          this._cancelLongPress()
        }

        if (this.draggingNodeIndex !== null && this.touchHasMoved) {
          const { x, y } = this._getTouchCoords(touch)
          this.nodes[this.draggingNodeIndex] = { x, y }
          this.redraw()
          return
        }

        if (this.touchHasMoved && this.draggingNodeIndex === null) {
          // Pan
          const dX = touch.clientX - this.lastPanPosition.x
          const dY = touch.clientY - this.lastPanPosition.y
          this.panOffset.x += dX / (this.scale * this.zoomLevel)
          this.panOffset.y += dY / (this.scale * this.zoomLevel)
          this.lastPanPosition = { x: touch.clientX, y: touch.clientY }
          this.redraw()
        }
      }
    },

    onTouchEnd (event) {
      this._cancelLongPress()
      const changedTouch = event.changedTouches && event.changedTouches[0]

      if (!this.touchHasMoved && !this.longPressFired && this.draggingNodeIndex === null && this.canEdit && changedTouch) {
        // Simple tap in empty area → add node
        const { x, y } = this._getTouchCoords(changedTouch)
        const nodeIndex = this.getNodeAtPosition(x, y)
        if (nodeIndex === null) {
          this.nodes.push({ x, y })
          this.redraw()
          this.$emit('nodes-change', this.nodes.length)
        }
      }

      this.draggingNodeIndex  = null
      this.isPanning          = false
      this.touchHasMoved      = false
      this.longPressFired     = false
      this.touchStartPos      = null
      this.pinchStartDistance = null
    }
  }
}
</script>

<style scoped>
.ipv-canvas-placeholder {
  position: absolute; inset: 0;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  background: #ececec; pointer-events: none;
}

.ipv-loading-indicator {
  position: absolute; inset: 0;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  background: rgba(255, 255, 255, 0.95); pointer-events: none;
  z-index: 10;
}

.ipv-pan-indicator {
  position: absolute; bottom: 12px; left: 12px;
  background: rgba(0,0,0,0.6); color: white;
  border-radius: 4px; padding: 4px 10px;
  display: flex; align-items: center;
  font-size: 12px; pointer-events: none;
}
</style>
